import React, { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, Float, Stars, PerspectiveCamera, Html } from '@react-three/drei';
import { PatientRecord, getStatusColor } from '@/lib/index';

interface VisualizationProps {
  data: PatientRecord[];
  width?: number | string;
  height?: number | string;
  interactive?: boolean;
}

/**
 * A 3D Scatter Plot mapping Age (X), BMI (Y), and Charges (Z).
 * Colors are based on patient status.
 */
export function ThreeDScatterPlot({ data, height = 400, interactive = true }: VisualizationProps) {
  const points = useMemo(() => {
    // Normalize values to fit in a 10x10x10 cube
    const maxAge = Math.max(...data.map(d => d.age)) || 1;
    const maxBMI = Math.max(...data.map(d => d.bmi)) || 1;
    const maxCharges = Math.max(...data.map(d => d.charges)) || 1;

    return data.slice(0, 50).map((d) => ({
      position: [
        (d.age / maxAge) * 10 - 5,
        (d.bmi / maxBMI) * 10 - 5,
        (d.charges / maxCharges) * 10 - 5,
      ] as [number, number, number],
      color: getStatusColor(d.status),
      label: `${d.name} (${d.age}y)`,
      id: d.id,
    }));
  }, [data]);

  return (
    <div style={{ height, width: '100%', borderRadius: 'var(--radius)', overflow: 'hidden' }} className="bg-card border border-border shadow-sm">
      <Canvas>
        <PerspectiveCamera makeDefault position={[12, 12, 12]} />
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        <Stars radius={100} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
        
        <group>
          {points.map((point, i) => (
            <PointItem key={point.id} {...point} />
          ))}
          
          {/* Grid/Axes */}
          <gridHelper args={[10, 10, 0x555555, 0x333333]} position={[0, -5, 0]} />
          <AxisLabels />
        </group>

        {interactive && <OrbitControls enableZoom={true} autoRotate autoRotateSpeed={0.5} />}
      </Canvas>
    </div>
  );
}

function PointItem({ position, color, label }: { position: [number, number, number], color: string, label: string }) {
  const [hovered, setHovered] = useState(false);
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.01;
      if (hovered) {
        meshRef.current.scale.lerp(new THREE.Vector3(1.5, 1.5, 1.5), 0.1);
      } else {
        meshRef.current.scale.lerp(new THREE.Vector3(1, 1, 1), 0.1);
      }
    }
  });

  return (
    <group position={position}>
      <mesh 
        ref={meshRef}
        onPointerOver={() => setHovered(true)}
        onPointerOut={() => setHovered(false)}
      >
        <sphereGeometry args={[0.2, 16, 16]} />
        <meshStandardMaterial 
          color={color} 
          emissive={color} 
          emissiveIntensity={hovered ? 2 : 0.5} 
          roughness={0.2} 
          metalness={0.8} 
        />
      </mesh>
      {hovered && (
        <Html distanceFactor={10}>
          <div className="bg-background/90 backdrop-blur-sm border border-border px-2 py-1 rounded text-[10px] whitespace-nowrap font-mono">
            {label}
          </div>
        </Html>
      )}
    </group>
  );
}

function AxisLabels() {
  return (
    <group>
      <Text position={[6, -5, 0]} fontSize={0.4} color="#888888">Age &rarr;</Text>
      <Text position={[-5.5, 0, 0]} fontSize={0.4} color="#888888" rotation={[0, 0, Math.PI / 2]}>BMI &rarr;</Text>
      <Text position={[0, -5, 6]} fontSize={0.4} color="#888888" rotation={[0, Math.PI / 2, 0]}>Charges &rarr;</Text>
    </group>
  );
}

/**
 * A 3D Network Graph representing patient correlations based on Region.
 */
export function ThreeDNetworkGraph({ data, height = 400, interactive = true }: VisualizationProps) {
  const graphData = useMemo(() => {
    const nodes = data.slice(0, 30).map((d, i) => ({
      id: d.id,
      pos: new THREE.Vector3(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10
      ),
      color: getStatusColor(d.status),
      region: d.region
    }));

    const links: [THREE.Vector3, THREE.Vector3][] = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        if (nodes[i].region === nodes[j].region) {
          links.push([nodes[i].pos, nodes[j].pos]);
        }
      }
    }

    return { nodes, links };
  }, [data]);

  return (
    <div style={{ height, width: '100%', borderRadius: 'var(--radius)', overflow: 'hidden' }} className="bg-card border border-border shadow-sm">
      <Canvas>
        <PerspectiveCamera makeDefault position={[0, 0, 15]} />
        <ambientLight intensity={0.4} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        
        <group>
          {graphData.nodes.map((node) => (
            <mesh key={node.id} position={node.pos}>
              <sphereGeometry args={[0.15, 16, 16]} />
              <meshStandardMaterial color={node.color} emissive={node.color} emissiveIntensity={0.5} />
            </mesh>
          ))}
          {graphData.links.map((link, i) => (
            <Line key={i} start={link[0]} end={link[1]} />
          ))}
        </group>

        {interactive && <OrbitControls autoRotate autoRotateSpeed={0.2} />}
      </Canvas>
    </div>
  );
}

function Line({ start, end }: { start: THREE.Vector3, end: THREE.Vector3 }) {
  const ref = useRef<THREE.LineSegments>(null);
  const geometry = useMemo(() => {
    return new THREE.BufferGeometry().setFromPoints([start, end]);
  }, [start, end]);

  return (
    <lineSegments ref={ref as any} geometry={geometry}>
      <lineBasicMaterial color="#444444" opacity={0.3} transparent />
    </lineSegments>
  );
}

/**
 * A 3D Volume Chart (Voxel-like representation of data density).
 */
export function ThreeDVolumeChart({ data, height = 400 }: VisualizationProps) {
  const grid = useMemo(() => {
    const size = 6;
    const result = [];
    for (let x = 0; x < size; x++) {
      for (let z = 0; z < size; z++) {
        // Randomly heightening based on data-like distribution simulation
        const val = Math.random() * 4 + 1;
        result.push({
          pos: [x - size / 2, val / 2 - 2, z - size / 2] as [number, number, number],
          h: val,
          color: `oklch(${0.5 + val * 0.05} 0.15 ${200 + val * 10})`
        });
      }
    }
    return result;
  }, []);

  return (
    <div style={{ height, width: '100%', borderRadius: 'var(--radius)', overflow: 'hidden' }} className="bg-card border border-border shadow-sm">
      <Canvas shadows>
        <PerspectiveCamera makeDefault position={[8, 8, 8]} />
        <ambientLight intensity={0.6} />
        <directionalLight 
          position={[5, 10, 5]} 
          intensity={1} 
          castShadow 
          shadow-mapSize-width={1024} 
          shadow-mapSize-height={1024} 
        />

        <group>
          {grid.map((cell, i) => (
            <mesh key={i} position={cell.pos} castShadow receiveShadow>
              <boxGeometry args={[0.8, cell.h, 0.8]} />
              <meshStandardMaterial color={cell.color} roughness={0.3} metalness={0.2} />
            </mesh>
          ))}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -2, 0]} receiveShadow>
            <planeGeometry args={[10, 10]} />
            <meshStandardMaterial color="#111111" />
          </mesh>
        </group>

        <OrbitControls />
      </Canvas>
    </div>
  );
}
